import {Injectable, inject} from '@angular/core'
import {Filters} from '@models/db.models'
import {Distributor} from '@models/distributor'
import {ConditionOffersTypes, OfferNM} from '@models/freeProducts'
import {LoadCatalogMethods} from '@models/order/loadCatalogMethods'
import {Order, OrderStatusCodes, OrderType} from '@models/order/order'
import {ORDER_TYPE_LINES, OrderProduct} from '@models/order/orderproduct'
import {Outlet} from '@models/outlet'
import {SaleConditionOptions, SaleableCondition} from '@models/product'
import {EmbeddedClientService} from '@service/embeddingService/embedded-client.service'
import {EmbeddedStoreService} from '@service/embeddingService/embedded-store.service'
import {NewOrderService} from './new-order.service'

/**
 * Variante embebida de `NewOrderService`.
 *
 * @description
 * En modo embebido el botón de volver se oculta (`showBackButton = false`) y el guardado
 * se delega al host vía `EmbeddingService.sendMessage('order::upsert', ...)`. Lee el
 * contexto de pedido (`orders`, `outlet`, `distributors`) desde `EmbeddedStoreService`
 * en lugar de PouchDB. El post-guardado devuelve el control al contenedor: la UI muestra
 * el estado "standby" esperando la siguiente acción del host.
 */
@Injectable({
  providedIn: 'root',
})
export class NewOrderEmbeddedService extends NewOrderService {
  override showBackButton = false
  readonly embeddingService: EmbeddedClientService = inject(EmbeddedClientService)
  private readonly embeddingStoreService: EmbeddedStoreService = inject(EmbeddedStoreService)

  sendSaveRequest = async (
    order: Order,
    activityId: string,
    outlet: Outlet,
    selectedDate: Date,
    email: string,
    orderState: string,
    distributor: Distributor
  ): Promise<void> => {
    const {createOrderRequest} = this.createOrderObjectRequest(
      order,
      outlet,
      distributor,
      selectedDate,
      email,
      this.orderModel.Type === OrderType.PEDIDO_TRANSFER
    )
    if (this.orderInfo.order) {
      createOrderRequest.id = this.orderInfo.order.id
      createOrderRequest.orderId = this.orderInfo.order.orderId
      createOrderRequest.orderDate = this.orderInfo.order.orderDate
      createOrderRequest.status = this.orderInfo.order.status
    } else {
      createOrderRequest.status = OrderStatusCodes.LISTO
    }
    createOrderRequest.insertionOrigin = 'UVE Platform'
    this.embeddingService.sendMessage('order::upsert', {
      ...createOrderRequest,
      desiredDeliveryDate: new Date(createOrderRequest.desiredDeliveryDate),
    })
  }

  override deleteOrder = async (order: Order): Promise<void> => {}

  protected override getSelectedOrder = (orders: Order[], orderId: string): Order | null => {
    return orders.find(o => o.id === orderId || o.orderId === orderId)
  }

  protected override getResponseDraftMode = async (
    draft: any
  ): Promise<{
    refreshTotalizations: {
      fetchOrderRequest: boolean
      refreshFreeProducts: boolean
    }
    loading: boolean
    noNetworkAlert: boolean
    error: boolean
  }> => {
    if (draft.value?.products) {
      await this.catalogService.getCatalogProducts('name', [
        {
          field: 'codes',
          condition: Filters.EQUALS,
          value: draft.value?.products.map(p => p.productCodeByManufacturer).join(','),
        },
      ])
    }
    return {
      refreshTotalizations: {
        fetchOrderRequest: this.onlineMethod,
        refreshFreeProducts: this.hasChanges,
      },
      loading: false,
      noNetworkAlert: false,
      error: false,
    }
  }

  protected override handleExistingOrder = async (): Promise<{
    refreshTotalizations: {
      fetchOrderRequest: boolean
      refreshFreeProducts: boolean
    }
    loading: boolean
    noNetworkAlert: boolean
    error: boolean
  }> => {
    this.distributors = this.orderInfo.distributors
    const products = await this.catalogService.getCatalogProducts('name', [
      {
        field: 'codes',
        condition: Filters.EQUALS,
        value: this.orderInfo.order.products?.map(p => p.productCodeByManufacturer).join(','),
      },
    ])

    if (this.orderModel?.Type === OrderType.PEDIDO_TRANSFER) {
      this.selectedDistributor = this.distributors.find((e: Distributor) => e.id === this.orderInfo.order.distributorId)
      this.selectedDistributorId = this.selectedDistributor?.id
    }
    const offerReadOnlyTypes = ['OP', 'N+M']
    const freeOrderLines =
      this.orderLineService.allOrderLines
        ?.filter(o => o.additionalInfo.uveId === ORDER_TYPE_LINES.FREE)
        .map(o => o.id) ?? []

    this.selectedProducts =
      this.orderInfo.order.products?.map((e: OrderProduct) => {
        const product = products.find(p => p.productCodeByManufacturer === e.productCodeByManufacturer)
        if (this.orderModel?.Valued && !this.orderLineService.isFreeTypeLine(e.typeLine)) {
          e.prices = product?.prices
        }
        return {
          ...e,
          availablesUMs: product?.availablesUMs,
          readOnly: offerReadOnlyTypes.includes(e.offerType) && freeOrderLines.includes(e.typeLine),
          offersApplied: e.agreements,
          allOffersApplied: e.agreements,
          umConvertions: product?.umConvertions,
          umOrderForConvertion: product?.umOrderForConvertion,
        }
      }) ?? []

    this.loadUpdateProducts()
    this.loadOrderLines()
    this.draftOrder = {
      classifiers: this.orderInfo.order.classifiers,
      observations: this.orderInfo.order.observations,
      date: this.orderInfo.order.desiredDeliveryDate,
    }

    return {
      refreshTotalizations: {
        fetchOrderRequest: this.onlineMethod,
        refreshFreeProducts: false,
      },
      loading: false,
      noNetworkAlert: false,
      error: false,
    }
  }

  protected override getFreeProductsByOfferType = (
    freeProducts: OfferNM[],
    offerType: ConditionOffersTypes
  ): OfferNM[] =>
    offerType === ConditionOffersTypes.OfferByPoints ?
      (freeProducts?.filter(o => o.agreement?.offerType === offerType) ?? [])
    : (freeProducts?.filter(o => !o.agreement || o.agreement?.offerType === offerType) ?? [])

  protected override preloadOutletDetail = async (outletId: string): Promise<void> => {
    this.outletDetailService.data = {
      orders: this.embeddingStoreService.orders(),
      loadedOutletId: outletId,
      groups: [],
      productsAssortments: {excludeAssortments: [], saleAssortments: []},
      codes: [],
      distributors: await this.outletDetailService.distributorService.getDistributorsForOrder(outletId),
      assortmentsAxis: [],
    }
    this.outletDetailService.loading = false
  }

  protected prepareNewOrderProductsForTemplate = (
    productCatalog: OrderProduct,
    product: OrderProduct
  ): OrderProduct => ({
    ...productCatalog,
    orderedUM: productCatalog?.orderedUM || product?.orderedUM,
    lastDateOrdered: product?.lastDateOrdered,
    lastPrice: product?.lastPrice,
    quantityOrdered: product?.quantityOrdered,
    quantityOrderedUMBase: product?.quantityOrderedUMBase,
    deliverySelected: false,
    typeLine: this.orderLineService.getDeliveryDefaultTypeLine(),
    orderString: `${product?.orderString} ${this.catalogService.getDefaultOrderProduct(product)}`,
    validations: null,
  })

  override prepareNewOrderInfo = async ({
    outlet,
    actionId,
    orderId,
    activityId,
    preloadStockLines = false,
  }: {
    outlet: Outlet
    actionId: string
    orderId: string
    activityId: string
    preloadStockLines?: boolean
  }): Promise<any> => {
    const result = await this.getOrderInfo(outlet, actionId, orderId, activityId)
    if (preloadStockLines) {
      await this.preloadStockLines(outlet, this.selectedProducts)
    }
    return result
  }

  override calculateSaleableConditions = (product: OrderProduct): SaleableCondition[] => {
    const catalogMethod = this.catalogService?.loadCatalogMethod
    let salableConditions = []
    if (
      (catalogMethod === LoadCatalogMethods.ASSORTMENT_ONLY.toString() ||
        catalogMethod === LoadCatalogMethods.ASSORTMENT.toString()) &&
      product?.appliedAssortmentsInfo?.length > 0
    ) {
      salableConditions = salableConditions.concat(
        product.appliedAssortmentsInfo?.map(assortment => ({
          type: SaleConditionOptions.ASSORTMENT,
          id: assortment.id,
          name: assortment.name,
        })) ?? []
      )
    }

    if (
      (catalogMethod === LoadCatalogMethods.PRICELIST.toString() ||
        catalogMethod === LoadCatalogMethods.ASSORTMENT.toString()) &&
      product?.pricelistId
    ) {
      salableConditions = salableConditions.concat(
        product.pricelistId?.split(',')?.map(value => ({
          type: SaleConditionOptions.PRICELIST,
          id: value,
          name: value,
        }))
      )
    }

    return salableConditions.filter(
      (value, index, array) =>
        !!value && array.findIndex(item => !!item && item.id === value.id && item.type === value.type) === index
    )
  }

  override canRefreshTotalizations = (products: OrderProduct[]): boolean => {
    const response = products.some(p => p.orderedAmount !== 0) // || this.firstLoad();
    this.firstLoad.set(false)
    return response
  }
}
